import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../../components/ui/Header";
import CategoryFilter from "./components/CategoryFilter";
import PortfolioGrid from "./components/PortfolioGrid";
import TestimonialSection from "./components/TestimonialSection";
import CrossCategoryDiscovery from "./components/CrossCategoryDiscovery";
import StatsSection from "./components/StatsSection";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";

const DynamicPortfolioEcosystem = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState("events");

  const categories = [
    {
      id: "events",
      name: "Events & Weddings",
      icon: "Heart",
      accentColor: "#E8B4A0",
      description:
        "Romantic, timeless storytelling that captures life's most precious moments with cinematic elegance.",
      projectCount: 150,
      rating: 4.9,
    },
    {
      id: "realestate",
      name: "Real Estate",
      icon: "Home",
      accentColor: "#4A90A4",
      description:
        "Clean, architectural presentations that showcase properties with sophisticated visual appeal.",
      projectCount: 200,
      rating: 4.8,
    },
    {
      id: "marketing",
      name: "Marketing & Brands",
      icon: "Target",
      accentColor: "#FF6B35",
      description:
        "Bold, contemporary content that cuts through noise and drives measurable engagement.",
      projectCount: 120,
      rating: 4.9,
    },
    {
      id: "design",
      name: "Design Fiction",
      icon: "Lightbulb",
      accentColor: "#00D4AA",
      description:
        "Experimental, futuristic concepts that visualize tomorrow's possibilities today.",
      projectCount: 80,
      rating: 5.0,
    },
  ];

  const projects = [
    // Events & Weddings
    {
      id: 1,
      category: "events",
      title: "Sarah & Michael's Garden Wedding",
      client: "Sarah & Michael Thompson",
      description:
        "An intimate garden ceremony captured with romantic cinematography and golden hour magic.",
      fullDescription: `Sarah and Michael's wedding was a celebration of love set against the backdrop of a stunning botanical garden. Our team captured every precious moment, from the emotional first look to the joyful reception dancing. Using natural lighting and cinematic techniques, we created a timeless love story that the couple will treasure forever.\n\nThe challenge was working with the natural garden lighting throughout the day, but our expertise in outdoor cinematography allowed us to capture the perfect golden hour shots and intimate moments that truly reflected their love story.`,
      thumbnail:
        "https://images.unsplash.com/photo-1519741497674-611481863552?w=800&h=600&fit=crop",
      duration: "8:45",
      views: 25400,
      year: 2024,
      location: "Napa Valley, CA",
      tags: [
        "Wedding",
        "Garden Ceremony",
        "Golden Hour",
        "Romantic",
        "Cinematic",
      ],
    },
    {
      id: 2,
      category: "events",
      title: "Corporate Gala Excellence",
      client: "Tech Innovations Inc.",
      description:
        "A sophisticated corporate event showcasing company achievements with elegant visual storytelling.",
      fullDescription: `Tech Innovations Inc.'s annual gala required a perfect blend of corporate professionalism and celebratory energy. We captured keynote speeches, award ceremonies, and networking moments that highlighted the company's achievements and culture.\n\nOur approach focused on creating content that could be used for both internal communications and external marketing, ensuring every shot served multiple purposes while maintaining the event's sophisticated atmosphere.`,
      thumbnail:
        "https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&h=600&fit=crop",
      duration: "12:30",
      views: 18200,
      year: 2024,
      location: "San Francisco, CA",
      tags: ["Corporate", "Gala", "Professional", "Awards", "Networking"],
    },
    {
      id: 3,
      category: "events",
      title: "Music Festival Highlights",
      client: "Sunset Music Festival",
      description:
        "High-energy festival coverage capturing the essence of live music and crowd excitement.",
      fullDescription: `The Sunset Music Festival presented unique challenges with multiple stages, varying lighting conditions, and massive crowds. Our team deployed across the venue to capture both intimate artist moments and the electric crowd energy.\n\nUsing specialized audio equipment and multiple camera angles, we created a dynamic highlight reel that perfectly captured the festival's atmosphere and the connection between artists and their audience.`,
      thumbnail:
        "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop",
      duration: "15:20",
      views: 42100,
      year: 2024,
      location: "Austin, TX",
      tags: [
        "Music Festival",
        "Live Performance",
        "Crowd Energy",
        "Multi-Stage",
        "Concert",
      ],
    },

    // Real Estate
    {
      id: 4,
      category: "realestate",
      title: "Luxury Penthouse Showcase",
      client: "Premier Properties Group",
      description:
        "Architectural cinematography highlighting the sophisticated design and city views of a premium penthouse.",
      fullDescription: `This luxury penthouse required a sophisticated approach to showcase its architectural details and stunning city views. Our team used specialized equipment including drones and stabilized camera systems to capture smooth, cinematic movements through the space.\n\nThe challenge was balancing the interior lighting with the bright city views, which we solved using advanced color grading techniques and strategic shooting times to capture the space at its most flattering moments.`,
      thumbnail:
        "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&h=600&fit=crop",
      duration: "6:15",
      views: 31500,
      year: 2024,
      location: "Manhattan, NY",
      tags: ["Luxury", "Penthouse", "City Views", "Architecture", "Premium"],
    },
    {
      id: 5,
      category: "realestate",
      title: "Modern Family Home Tour",
      client: "Residential Realty Co.",
      description:
        "Warm, inviting presentation of a family home emphasizing comfort and modern amenities.",
      fullDescription: `This modern family home needed to appeal to potential buyers by showcasing both its contemporary design and family-friendly features. We focused on creating a warm, welcoming atmosphere while highlighting the home's modern amenities and thoughtful layout.\n\nOur approach included capturing natural family moments in the space to help viewers envision their own lives in the home, combined with clean architectural shots that emphasized the quality of construction and design.`,
      thumbnail:
        "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&h=600&fit=crop",
      duration: "8:30",
      views: 19800,
      year: 2024,
      location: "Palo Alto, CA",
      tags: [
        "Family Home",
        "Modern Design",
        "Residential",
        "Comfort",
        "Lifestyle",
      ],
    },
    {
      id: 6,
      category: "realestate",
      title: "Commercial Space Transformation",
      client: "Urban Development LLC",
      description:
        "Before and after showcase of a commercial renovation highlighting potential and transformation.",
      fullDescription: `This commercial space transformation project required us to tell a compelling before-and-after story that would attract potential tenants and investors. We documented the renovation process and created a compelling narrative about the space's potential.\n\nThe final video showcased not just the physical transformation, but also the vision for how businesses could thrive in the renovated space, including testimonials from the development team and architectural insights.`,
      thumbnail:
        "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop",
      duration: "10:45",
      views: 15600,
      year: 2024,
      location: "Chicago, IL",
      tags: [
        "Commercial",
        "Renovation",
        "Transformation",
        "Investment",
        "Business",
      ],
    },

    // Marketing & Brands
    {
      id: 7,
      category: "marketing",
      title: "Tech Startup Launch Campaign",
      client: "InnovateTech Solutions",
      description:
        "Bold, energetic campaign video introducing a revolutionary tech product to the market.",
      fullDescription: `InnovateTech Solutions needed a launch campaign that would cut through the noise in the competitive tech market. We created a bold, visually striking campaign that combined product demonstrations with compelling storytelling about the problem their technology solves.\n\nThe campaign included multiple video formats optimized for different platforms, from 60-second TV spots to 15-second social media teasers, all maintaining consistent branding and messaging while maximizing engagement across channels.`,
      thumbnail:
        "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop",
      duration: "2:30",
      views: 156000,
      year: 2024,
      location: "Silicon Valley, CA",
      tags: [
        "Tech Launch",
        "Startup",
        "Product Demo",
        "Innovation",
        "Campaign",
      ],
    },
    {
      id: 8,
      category: "marketing",
      title: "Fashion Brand Storytelling",
      client: "Ethereal Fashion House",
      description:
        "Artistic brand film capturing the essence and philosophy of a luxury fashion label.",
      fullDescription: `Ethereal Fashion House wanted to communicate their brand philosophy and craftsmanship through visual storytelling. We created an artistic brand film that showcased their design process, attention to detail, and the emotion behind each piece.\n\nThe project required close collaboration with their creative team to ensure the video aligned with their brand aesthetic while appealing to their target demographic of fashion-conscious consumers who value artisanal quality.`,
      thumbnail:
        "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=600&fit=crop",
      duration: "4:15",
      views: 89200,
      year: 2024,
      location: "Los Angeles, CA",
      tags: ["Fashion", "Brand Story", "Luxury", "Artisanal", "Creative"],
    },
    {
      id: 9,
      category: "marketing",
      title: "Restaurant Chain Rebrand",
      client: "Fresh Eats Restaurant Group",
      description:
        "Appetizing food cinematography and brand refresh campaign for a growing restaurant chain.",
      fullDescription: `Fresh Eats Restaurant Group was undergoing a complete rebrand and needed video content that would showcase their new direction while making their food irresistibly appealing. We combined mouth-watering food cinematography with lifestyle elements that reflected their target demographic.\n\nThe challenge was creating content that worked across multiple restaurant concepts within their portfolio while maintaining brand consistency. We developed a flexible visual language that could adapt to different cuisines while maintaining the Fresh Eats identity.`,
      thumbnail:
        "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop",
      duration: "3:45",
      views: 67800,
      year: 2024,
      location: "Miami, FL",
      tags: [
        "Food & Beverage",
        "Rebrand",
        "Restaurant",
        "Lifestyle",
        "Culinary",
      ],
    },

    // Design Fiction
    {
      id: 10,
      category: "design",
      title: "Future City Concept",
      client: "Urban Planning Institute",
      description:
        "Visionary exploration of sustainable urban living in 2050 through immersive visual storytelling.",
      fullDescription: `The Urban Planning Institute commissioned us to create a compelling vision of sustainable urban living in 2050. This project pushed the boundaries of visual storytelling, combining real architectural elements with speculative design and advanced visual effects.\n\nWe worked closely with urban planners, architects, and environmental scientists to ensure our vision was both inspiring and grounded in realistic projections of technological and social development. The result is a thought-provoking piece that has influenced policy discussions and urban development projects.`,
      thumbnail:
        "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop",
      duration: "7:20",
      views: 234000,
      year: 2024,
      location: "Conceptual",
      tags: [
        "Future City",
        "Sustainability",
        "Urban Planning",
        "Speculative Design",
        "Environmental",
      ],
    },
    {
      id: 11,
      category: "design",
      title: "AI Consciousness Exploration",
      client: "Future Tech Research Lab",
      description:
        "Philosophical journey into artificial consciousness through abstract visual metaphors and cutting-edge graphics.",
      fullDescription: `This experimental piece explores the philosophical implications of artificial consciousness through abstract visual metaphors and cutting-edge graphics. Working with researchers and philosophers, we created a visual language that makes complex concepts accessible and emotionally engaging.\n\nThe project required developing new visualization techniques to represent abstract concepts like consciousness, learning, and artificial emotion. The result challenges viewers to consider the future relationship between humans and artificial intelligence.`,
      thumbnail:
        "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop",
      duration: "9:15",
      views: 187500,
      year: 2024,
      location: "Conceptual",
      tags: ["AI", "Consciousness", "Philosophy", "Abstract", "Future Tech"],
    },
    {
      id: 12,
      category: "design",
      title: "Space Colony Visualization",
      client: "Aerospace Innovation Corp",
      description:
        "Realistic depiction of human life in space colonies, blending scientific accuracy with inspiring vision.",
      fullDescription: `Aerospace Innovation Corp needed a realistic yet inspiring visualization of human life in space colonies to support their research funding proposals and public engagement efforts. We combined scientific accuracy with compelling storytelling to create a vision that feels both achievable and exciting.\n\nWorking with aerospace engineers and space psychologists, we addressed practical challenges of space living while maintaining an optimistic vision of humanity's future among the stars. The video has been used in educational programs and policy presentations worldwide.`,
      thumbnail:
        "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=800&h=600&fit=crop",
      duration: "11:30",
      views: 312000,
      year: 2024,
      location: "Conceptual",
      tags: [
        "Space Colony",
        "Aerospace",
        "Future Living",
        "Scientific",
        "Exploration",
      ],
    },
  ];

  const testimonials = [
    // Events testimonials
    {
      id: 1,
      category: "events",
      name: "Sarah Thompson",
      role: "Bride",
      company: "Thompson-Johnson Wedding",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      content:
        "Skycrest Studios captured our wedding day with such artistry and emotion. Every time we watch our wedding film, we fall in love all over again. They truly understood our vision and brought it to life beautifully.",
      rating: 5,
      date: "June 2024",
      projectType: "Garden Wedding",
      isVideoTestimonial: true,
    },
    {
      id: 2,
      category: "events",
      name: "Jennifer Martinez",
      role: "Event Coordinator",
      company: "Elite Events Planning",
      avatar: "https://randomuser.me/api/portraits/women/45.jpg",
      content:
        "Working with Skycrest on multiple corporate events has been exceptional. Their professionalism and ability to capture the essence of each event while staying unobtrusive is remarkable.",
      rating: 5,
      date: "August 2024",
      projectType: "Corporate Gala",
      isVideoTestimonial: false,
    },
    {
      id: 3,
      category: "events",
      name: "David Chen",
      role: "Festival Director",
      company: "Sunset Music Festival",
      avatar: "https://randomuser.me/api/portraits/men/28.jpg",
      content:
        "The energy and excitement of our festival was perfectly captured by Skycrest. Their team worked seamlessly across multiple stages and delivered content that exceeded our expectations.",
      rating: 5,
      date: "July 2024",
      projectType: "Music Festival",
      isVideoTestimonial: true,
    },

    // Real Estate testimonials
    {
      id: 4,
      category: "realestate",
      name: "Robert Williams",
      role: "Senior Broker",
      company: "Premier Properties Group",
      avatar: "https://randomuser.me/api/portraits/men/42.jpg",
      content:
        "The luxury penthouse video increased our showing requests by 300%. Skycrest's architectural cinematography showcased the property in a way that photos simply couldn't match.",
      rating: 5,
      date: "September 2024",
      projectType: "Luxury Penthouse",
      isVideoTestimonial: false,
    },
    {
      id: 5,
      category: "realestate",
      name: "Lisa Anderson",
      role: "Real Estate Agent",
      company: "Residential Realty Co.",
      avatar: "https://randomuser.me/api/portraits/women/38.jpg",
      content:
        "The family home tour video helped potential buyers connect emotionally with the property. We sold the house 40% faster than average, and I believe the video was a key factor.",
      rating: 5,
      date: "October 2024",
      projectType: "Family Home",
      isVideoTestimonial: true,
    },
    {
      id: 6,
      category: "realestate",
      name: "Michael Torres",
      role: "Development Manager",
      company: "Urban Development LLC",
      avatar: "https://randomuser.me/api/portraits/men/35.jpg",
      content:
        "The before-and-after transformation video was instrumental in securing tenants for our commercial space. Skycrest understood our vision and translated it into compelling visual storytelling.",
      rating: 5,
      date: "November 2024",
      projectType: "Commercial Renovation",
      isVideoTestimonial: false,
    },

    // Marketing testimonials
    {
      id: 7,
      category: "marketing",
      name: "Alex Kumar",
      role: "CEO",
      company: "InnovateTech Solutions",
      avatar: "https://randomuser.me/api/portraits/men/29.jpg",
      content:
        "Our product launch campaign exceeded all expectations. The video generated 2.5M views in the first month and directly contributed to a 150% increase in pre-orders. Skycrest delivered exactly what we needed.",
      rating: 5,
      date: "March 2024",
      projectType: "Product Launch",
      isVideoTestimonial: true,
    },
    {
      id: 8,
      category: "marketing",
      name: "Isabella Rodriguez",
      role: "Creative Director",
      company: "Ethereal Fashion House",
      avatar: "https://randomuser.me/api/portraits/women/31.jpg",
      content:
        "The brand film perfectly captured our philosophy and aesthetic. It's been our most successful piece of marketing content, generating significant buzz in fashion circles and increasing brand awareness by 200%.",
      rating: 5,
      date: "May 2024",
      projectType: "Brand Film",
      isVideoTestimonial: false,
    },
    {
      id: 9,
      category: "marketing",
      name: "Carlos Mendoza",
      role: "Marketing Director",
      company: "Fresh Eats Restaurant Group",
      avatar: "https://randomuser.me/api/portraits/men/44.jpg",
      content:
        "The rebrand campaign video helped us successfully transition our image while maintaining customer loyalty. Foot traffic increased 25% across all locations after the campaign launch.",
      rating: 5,
      date: "April 2024",
      projectType: "Rebrand Campaign",
      isVideoTestimonial: true,
    },

    // Design Fiction testimonials
    {
      id: 10,
      category: "design",
      name: "Dr. Elena Vasquez",
      role: "Research Director",
      company: "Urban Planning Institute",
      avatar: "https://randomuser.me/api/portraits/women/47.jpg",
      content:
        "The Future City concept video has become a cornerstone of our public engagement efforts. It's been viewed by policymakers worldwide and has influenced several urban development initiatives.",
      rating: 5,
      date: "January 2024",
      projectType: "Future Visualization",
      isVideoTestimonial: false,
    },
    {
      id: 11,
      category: "design",
      name: "Dr. James Park",
      role: "Lead Researcher",
      company: "Future Tech Research Lab",
      avatar: "https://randomuser.me/api/portraits/men/52.jpg",
      content:
        "The AI consciousness exploration pushed the boundaries of how we communicate complex philosophical concepts. It's been featured in academic conferences and has sparked important discussions about AI ethics.",
      rating: 5,
      date: "February 2024",
      projectType: "Conceptual Exploration",
      isVideoTestimonial: true,
    },
    {
      id: 12,
      category: "design",
      name: "Commander Sarah Mitchell",
      role: "Mission Planner",
      company: "Aerospace Innovation Corp",
      avatar: "https://randomuser.me/api/portraits/women/41.jpg",
      content:
        "The space colony visualization has been invaluable for our funding presentations and public outreach. It makes our mission tangible and inspiring, helping people understand why space exploration matters.",
      rating: 5,
      date: "December 2023",
      projectType: "Space Visualization",
      isVideoTestimonial: false,
    },
  ];

  useEffect(() => {
    const categoryParam = searchParams?.get("category");
    if (categoryParam && categories?.find((cat) => cat?.id === categoryParam)) {
      setActiveCategory(categoryParam);
    }
  }, [searchParams]);

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setSearchParams({ category: categoryId });
  };

  const currentCategory = categories?.find((cat) => cat?.id === activeCategory);

  const getHeroStyle = () => {
    switch (activeCategory) {
      case "events":
        return "bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50";
      case "realestate":
        return "bg-gradient-to-br from-blue-50 via-slate-50 to-gray-50";
      case "marketing":
        return "bg-gradient-to-br from-orange-50 via-red-50 to-pink-50";
      case "design":
        return "bg-gradient-to-br from-gray-900 via-black to-gray-800";
      default:
        return "bg-gradient-to-br from-gray-50 to-white";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section
        className={`pt-24 mt-14 pb-16 ${getHeroStyle()} transition-all duration-1000`}
      >
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1
              className={`text-5xl md:text-6xl font-bold mb-6 ${
                activeCategory === "design" ? "text-white" : "text-gray-900"
              }`}
            >
              Portfolio
            </h1>

            <p
              className={`text-xl md:text-2xl mb-8 leading-relaxed ${
                activeCategory === "design" ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Experience Skycrest's chameleon-like adaptability across
              industries. Each category transforms our entire visual identity,
              demonstrating our mastery of diverse storytelling approaches.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-6 mb-12">
              <div className="flex items-center space-x-2">
                <Icon
                  name="Eye"
                  size={20}
                  color={activeCategory === "design" ? "#9CA3AF" : "#6B7280"}
                />
                <span
                  className={`text-sm ${
                    activeCategory === "design"
                      ? "text-gray-400"
                      : "text-gray-600"
                  }`}
                >
                  {currentCategory?.projectCount}+ Projects
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon
                  name="Star"
                  size={20}
                  color={activeCategory === "design" ? "#9CA3AF" : "#6B7280"}
                />
                <span
                  className={`text-sm ${
                    activeCategory === "design"
                      ? "text-gray-400"
                      : "text-gray-600"
                  }`}
                >
                  {currentCategory?.rating} Rating
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon
                  name="Award"
                  size={20}
                  color={activeCategory === "design" ? "#9CA3AF" : "#6B7280"}
                />
                <span
                  className={`text-sm ${
                    activeCategory === "design"
                      ? "text-gray-400"
                      : "text-gray-600"
                  }`}
                >
                  Industry Recognition
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Category Filter */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <CategoryFilter
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />
        </div>
      </section>
      {/* Portfolio Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {currentCategory?.name} Portfolio
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {currentCategory?.description}
            </p>
          </div>

          <PortfolioGrid
            projects={projects}
            activeCategory={activeCategory}
            categories={categories}
          />
        </div>
      </section>
      {/* Stats Section */}
      <StatsSection activeCategory={activeCategory} categories={categories} />
      {/* Testimonials */}
      <TestimonialSection
        testimonials={testimonials}
        activeCategory={activeCategory}
        categories={categories}
      />
      {/* Cross-Category Discovery */}
      <CrossCategoryDiscovery
        categories={categories}
        activeCategory={activeCategory}
        projects={projects}
      />
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-black">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Vision?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how Skycrest Studios can adapt our expertise to tell
            your unique story with the perfect visual language for your
            industry.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button
              variant="default"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              className="bg-cta hover:bg-cta/90 text-cta-foreground"
            >
              Schedule Consultation
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              className="border-white text-white hover:bg-white hover:text-gray-900"
            >
              Contact Us Today
            </Button>
          </div>
        </div>
      </section>
      {/* Footer (white background, dark text) */}
      <footer className="bg-background text-foreground py-12 border-t border-border/50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-lg font-brand text-primary">
                  <span>Skycrest</span>
                  <span className="text-accent">Films</span>
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Visual storytelling mastery across diverse industries, adapting
                our expertise to authentically represent each client's world.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-foreground">Services</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <a href="#" className="hover:text-accent transition-colors">
                    Event Videography
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-accent transition-colors">
                    Real Estate Marketing
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-accent transition-colors">
                    Brand Campaigns
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-accent transition-colors">
                    Design Fiction
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-foreground">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <a href="#" className="hover:text-accent transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-accent transition-colors">
                    Our Team
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-accent transition-colors">
                    Case Studies
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-accent transition-colors">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-foreground">Connect</h4>
              <div className="flex space-x-4 mb-4">
                <a
                  href="#"
                  className="text-muted-foreground hover:text-accent transition-colors"
                >
                  <Icon name="Instagram" size={20} />
                </a>
                <a
                  href="#"
                  className="text-muted-foreground hover:text-accent transition-colors"
                >
                  <Icon name="Youtube" size={20} />
                </a>
                <a
                  href="#"
                  className="text-muted-foreground hover:text-accent transition-colors"
                >
                  <Icon name="Linkedin" size={20} />
                </a>
              </div>
              <p className="text-sm text-muted-foreground">
                hello@skycreststudios.com
                <br />
                (508) 980-5187
              </p>
            </div>
          </div>

          <div className="border-t border-border/50 mt-8 pt-8 text-center">
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} SkycrestFilms. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DynamicPortfolioEcosystem;
